import React, { KeyboardEventHandler, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { Navigation, Thumbs, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import type { ImageVariant } from 'src/models/IImage';

import { Icon } from '@nebo-team-dev/vobaza.ui.icon/dist';
import ClientOnlyPortal from './ui/ClientOnlyPortal';

import styles from './styles.module.scss';

interface IFullImageSwiperProps {
  images: ImageVariant[];
  onClose: (index: number) => void;
  isOpen: boolean;
  thumbsImages: ImageVariant[];
  currentSlide: number;
}

const FullImageSwiper = ({ images, onClose, isOpen, thumbsImages, currentSlide }: IFullImageSwiperProps) => {
  const [fullScreenThumbsSwiper, setFullScreenThumbsSwiper] = useState(null);
  const [fullScreenSwiper, setFullScreenSwiper] = useState(null);
  const [swiperClassList, setSwiperClassList] = useState(
    `${styles.backdrop} ${isOpen ? styles.backdropActive : styles.backdropHidden}`
  );

  const onKeyPress: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      fullScreenSwiper.slidePrev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      fullScreenSwiper.slideNext();
    }
  };

  const slideRef = useRef(null);

  const renderFullImages = () => {
    return images.map((img) => (
      <SwiperSlide key={`full-slide-${img.id}`}>
        <Image
          className={styles.fullSlideImage}
          src={img.url}
          width={img.meta.width}
          height={img.meta.height}
          style={{ objectFit: 'contain' }}
          alt="swiper image"
          priority
        />
      </SwiperSlide>
    ));
  };

  const renderThumbImages = () => {
    return thumbsImages.map((img) => (
      <SwiperSlide key={`thumb-slide-${img.id}`} className={styles.fullThumbsSlide}>
        <Image
          src={img.url}
          width={img.meta.width}
          height={img.meta.height}
          style={{ objectFit: 'contain', objectPosition: 'center' }}
          alt="swiper image"
        />
      </SwiperSlide>
    ));
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isOpen) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        fullScreenSwiper.slidePrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        fullScreenSwiper.slideNext();
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [isOpen, fullScreenSwiper]);

  useEffect(() => {
    if (fullScreenSwiper && !fullScreenSwiper?.destroyed) {
      if (isOpen) fullScreenSwiper.slideToLoop(currentSlide, 0);
      setSwiperClassList(`${styles.backdrop} ${isOpen ? styles.backdropActive : styles.backdropHidden}`);
    }
  }, [isOpen, fullScreenSwiper, currentSlide]);

  return (
    <ClientOnlyPortal selector="body">
      <div className={swiperClassList} id="full-image-swiper" onKeyDown={onKeyPress}>
        <div className={styles.modal}>
          <button
            className={styles.closeButton}
            onClick={() => {
              onClose(slideRef.current);
            }}
          >
            <Icon name="Cross" />
          </button>
          <>
            <Swiper
              className={styles.fullSwiper}
              modules={[Navigation, Thumbs, Pagination]}
              speed={220}
              loop={images.length && images.length > 1}
              allowTouchMove
              centeredSlides
              preloadImages
              navigation={{
                prevEl: '.product-swiper__prev',
                nextEl: '.product-swiper__next',
              }}
              thumbs={{
                swiper: fullScreenThumbsSwiper && !fullScreenThumbsSwiper?.destroyed ? fullScreenThumbsSwiper : null,
              }}
              pagination={{
                el: '.product-images-pagination',
                type: 'bullets',
              }}
              onActiveIndexChange={(swiper) => {
                slideRef.current = swiper.activeIndex - 1;
              }}
              onSwiper={(swiper) => setFullScreenSwiper(swiper)}
            >
              {Boolean(images.length) && renderFullImages()}
            </Swiper>
            <button className={`${styles.fullThumbsNavButton} product-swiper__prev`}>
              <Icon name="ArrowLeft" />
            </button>
            <button className={`${styles.fullThumbsNavButton} ${styles.fullThumbsNavButtonNext} product-swiper__next`}>
              <Icon name="ArrowRight" />
            </button>
          </>
          <>
            <div className={`product-images-pagination`} />
            <div className={styles.thumbsSwiperFull}>
              <Swiper
                className={styles.swiper}
                modules={[Thumbs]}
                watchSlidesProgress
                slidesPerView={'auto'}
                centerInsufficientSlides
                spaceBetween={8}
                speed={500}
                slideToClickedSlide
                onSwiper={setFullScreenThumbsSwiper}
              >
                {Boolean(thumbsImages.length) && renderThumbImages()}
              </Swiper>
            </div>
          </>
        </div>
      </div>
    </ClientOnlyPortal>
  );
};

export { FullImageSwiper };
